import { tgxApi } from "../../../app/tgxApi";
import { getFileName, serialize } from "../../../helpers/utils/Utils";
import {
    getAuthorizationHeader,
    getFileDownloadHeaders,
    handleQueryError,
    handleQueryErrorAndSuccess
} from "../../../helpers/utils/RtkQueryUtils";
import { downloadFile } from "../../../helpers/utils/FileUtils";

export const teamsApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({
        searchTeams: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/teams?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['teams'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        searchUserTeams: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/users?tenant?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['teams'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        createTeam: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/submit/team`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['teams'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Team")
            },
        }),
        getTeamById: build.query({
            query: (teamId) => {
                return {
                    url: `/ajax/get-team-by-id/${teamId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["teamById"]
        }),

        updateTeam: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/team/update/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['teams', 'teamById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Team")
            },
        }),
        teamMembers: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/get-team-member?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['teamMembers'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        addNewMemberIntoTeam: build.mutation({
            query: ({ teamId, userId }) => {
                return {
                    url: `/ajax/add/team/member/${userId}/${teamId}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teamMembers'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Added", "Team Member")
            },
        }),

        removeMemberFromTeam: build.mutation({
            query: ({ teamId, userId }) => {
                return {
                    url: `/ajax/delete-team-member-by-id/${userId}/${teamId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teamMembers'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Removed", "Team Member")
            },
        }),

        teamLeaders: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/get-team-manager?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['teamLeaders'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        addNewLeaderIntoTeam: build.mutation({
            query: ({ teamId, userId }) => {
                return {
                    url: `/ajax/add/team/manager/${userId}/${teamId}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teamLeaders'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Added", "Team Leader")
            },
        }),

        removeLeaderFromTeam: build.mutation({
            query: ({ teamId, userId }) => {
                return {
                    url: `/ajax/delete-team-manager-by-id/${userId}/${teamId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teamLeaders'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Removed", "Team Leader")
            },
        }),

        teamRules: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/team-rules?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['teamRules'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        deleteTeamRule: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/team-rules/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teamRules'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Team Rule")
            },
        }),
        deleteTeam: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/team/delete/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['teams'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Team")
            },
        }),

        createTeamRule: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/create/teamRule`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['teamRules'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Added", "Team Rule")
            },
        }),

        getTeamRuleById: build.query({
            query: (teamId) => {
                return {
                    url: `/ajax/get-team-rule-by-id/${teamId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["teamRuleById"]
        }),


        getTeamByTenant: build.query({
            query: (tenant) => {
                return {
                    url: `/ajax/teams-list-by-tenant/${tenant}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),


        updateTeamRule: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/update/team-rule/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['teamRules', 'teamRuleById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Team Rule")
            },
        }),

        getAllAssignToMembers: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);

                return {
                    url: `/ajax/get-assign-to-member?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getPreviewOrderList: build.query({
            query: ({ type, teamId, teamRuleId, count }) => {
    
                return {
                    url: `/ajax/get-rule-preview-result/${type}/${teamId}/${teamRuleId}/${count}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags:["getPreviewOrderList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getAllAssignProductToMembers: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);

                return {
                    url: `/ajax/teams/assign-to-members?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getAllAssignToLeaders: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);

                return {
                    url: `/ajax/teams/assign-to-leaders?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        exportTeamData: build.query({
            query: (query) => {
                return {
                    url: `/export/team`,
                    method: 'POST',
                    params: query,
                    headers: getFileDownloadHeaders(),
                    responseType: 'arraybuffer',
                    responseHandler: async (response: { blob: () => any; }) => {
                        const blob = await response.blob();
                        downloadFile(blob, getFileName("teams"))
                        return { success: true };
                    },
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

    })
});

export const {
    useSearchTeamsQuery,
    useCreateTeamMutation,
    useGetTeamByIdQuery,
    useUpdateTeamMutation,
    useTeamMembersQuery,
    useAddNewMemberIntoTeamMutation,
    useRemoveMemberFromTeamMutation,
    useTeamLeadersQuery,
    useAddNewLeaderIntoTeamMutation,
    useRemoveLeaderFromTeamMutation,
    useTeamRulesQuery,
    useDeleteTeamRuleMutation,
    useCreateTeamRuleMutation,
    useUpdateTeamRuleMutation,
    useGetTeamRuleByIdQuery,
    useGetAllAssignToMembersQuery,
    useGetAllAssignToLeadersQuery,
    useLazyExportTeamDataQuery,
    useGetAllAssignProductToMembersQuery,
    useSearchUserTeamsQuery,
    useLazyGetPreviewOrderListQuery,
    useDeleteTeamMutation,
    useGetTeamByTenantQuery
} = teamsApi;